import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { tabsClasses } from '@mui/material/Tabs';
import { Box, LinearProgress, Tab, Tabs, Typography } from '@mui/material';

import useNotification from '../../../../core/hooks/use_notification';
import { ROUTE_EMPLOYERS } from '../../../../core/constants';
import EmployerForm from './forms/employer_form';
import { getEmployerBillingScheduleNotificationData, getEmployerDiscountNotificationData, getEmployerNotificationData, getHubsNotificationData, getPodsNotificationData, getPopsNotificationData } from '../../../../core/utils/notification_utils';
import { getEmployerAction, getEmployerState } from '../../../../features/benefit/get_employer';
import ReportForm from './forms/report_form';
import DiscountForm from './forms/discount_form';
import { getEmployerDiscountAction, getEmployerDiscountState } from '../../../../features/benefit/get_employer_discount';
import { getAllPodsAction, getAllPodsState } from '../../../../features/pod/get_all_pods';
import { getAllPopsAction, getAllPopsState } from '../../../../features/pop/get_all_pops';
import { getAllHubsAction, getAllHubsState } from '../../../../features/pop/get_all_hubs';
import { getEmployerBillingScheduleAction, getEmployerBillingScheduleState } from '../../../../features/benefit/get_employer_billing_schedule';
import PageHeader from '../../../common/page_header';

const EmployerPage = () => {
    const { showErrorMessage } = useNotification();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { employerId } = useParams();

    const getEmployer = useSelector(getEmployerState);
    const getEmployerBillingSchedule = useSelector(getEmployerBillingScheduleState);
    const getAllPods = useSelector(getAllPodsState);
    const getAllPops = useSelector(getAllPopsState);
    const getAllHubs = useSelector(getAllHubsState);
    const getEmployerDiscount = useSelector(getEmployerDiscountState);

    const [tab, setTab] = useState(0);

    const refreshDiscount = useCallback(() => {
        dispatch(getEmployerDiscountAction(employerId))
            .then(response => showErrorMessage(getEmployerDiscountNotificationData(response)));
    }, [dispatch, employerId, showErrorMessage]);

    const refreshEmployer = useCallback(() => {
        dispatch(getEmployerAction(employerId))
            .then(response => showErrorMessage(getEmployerNotificationData(response)));
        dispatch(getEmployerBillingScheduleAction(employerId))
            .then(response => showErrorMessage(getEmployerBillingScheduleNotificationData(response)));
        dispatch(getAllPodsAction())
            .then(response => showErrorMessage(getPodsNotificationData(response)));
        dispatch(getAllPopsAction())
            .then(response => showErrorMessage(getPopsNotificationData(response)));
        dispatch(getAllHubsAction())
            .then(response => showErrorMessage(getHubsNotificationData(response)));
        refreshDiscount();
    }, [dispatch, employerId, refreshDiscount, showErrorMessage]);

    useEffect(() => {
        refreshEmployer();
    }, [refreshEmployer]);

    const renderView = () => {
        if (getEmployer?.loading || getEmployerBillingSchedule?.loading || getAllPods?.loading || getAllPops?.loading || getAllHubs?.loading || getEmployerDiscount?.loading) {
            return (
                <Box mt={ 6 }>
                    <LinearProgress/>
                </Box>
            );
        }

        if (getEmployer?.data && getEmployerBillingSchedule?.data && getAllPods?.data && getEmployerDiscount?.data) {
            if (tab === 0) {
                return <EmployerForm refreshEmployer={ refreshEmployer }/>;
            } else if (tab === 1) {
                return <DiscountForm employerId={ employerId } refreshDiscount={ refreshDiscount }/>;
            } else if (tab === 2) {
                return <ReportForm/>;
            }
        }

        return <></>;
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 } }>
            <PageHeader
                title={ getEmployer?.data?.name || 'Edycja produktu' }
                showBack={ true }
                onBack={ () => navigate(ROUTE_EMPLOYERS) }
                titleAdornment={
                    employerId && getEmployer?.data && (
                        <Typography variant="h6" color="textSecondary">
                            ID: { employerId }
                        </Typography>
                    )
                }
            />

            <Box sx={ { borderBottom: 1, borderColor: 'divider' } }>
                <Tabs
                    value={ tab }
                    onChange={ (_, value) => setTab(value) }
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={ {
                        [`& .${ tabsClasses.scrollButtons }`]: {
                            '&.Mui-disabled': { opacity: 0.3 },
                        },
                    } }
                >
                    <Tab label="Informacje ogólne"/>
                    <Tab label="Obniżka"/>
                    <Tab label="Raporty"/>
                </Tabs>
            </Box>

            <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1 } }>
                { renderView() }
            </Box>
        </Box>
    );
};

export default EmployerPage;